/**
 * What: Shopify study merchant-facing copy.
 * Transport / data-flow: Pure string dictionary segment merged by shopify-component-copy.ts for Shopify UI consumers.
 *
 */
import { studyBuilderFieldPickerCopy } from './study-builder-field-picker.js';
import { studyBuilderGuidanceCopy } from './study-builder-guidance.js';
import { studyBuilderPageCopy } from './study-builder-page.js';
import { studyBuilderReadinessCopy } from './study-builder-readiness.js';
export const shopifyComponentStudyCopy = {
    studyBuilderGuidance: studyBuilderGuidanceCopy,
    ...studyBuilderReadinessCopy,
    studyBuilderImages: {
        notImage: 'Choose an image file.',
        tooLarge: 'Variation images must be {mb} MB or smaller.',
    },
    studyBuilderPriceSuggestions: {
        intro: 'Starting points for comparison, calculated from your current price.',
        plus10: { title: 'Smaller price increase', delta: '+10%', why: 'Explore reactions to a smaller price increase.' },
        plus20: { title: 'Larger price increase', delta: '+20%', why: 'Explore reactions to a larger price increase.' },
        minus15: { title: 'Lower price', delta: '-15%', why: 'Explore whether a lower price changes purchase interest.' },
        charm: {
            title: 'Alternative price ending',
            delta: 'Charm',
            why: 'Explore a .99 price ending. This also changes the amount.',
        },
        use: 'Use this price',
        added: 'Added',
    },
    studyBuilderFields: {
        chooseComparison: 'Choose a comparison',
        setupHeading: 'Test setup',
        review: 'Review product fields',
        scope: 'Field meanings apply across your store, including other tests. They are saved separately from this draft.',
        saved: 'Field meaning saved across your store. Discarding draft changes will not undo it.',
        save: 'Save field meanings',
        confirmSave: 'Confirm and save',
        backToFields: 'Back to fields',
        confirmScope: 'Save this meaning across your store?',
        confirmConsequences: 'This may make selected comparisons unavailable. Your comparison writing and audience settings will be kept. After reloading product fields, you will be asked before all added questions are cleared and managed questions are rebuilt. Declining that reset keeps your writing and blocks preparation; it does not undo the store setting.',
        field: 'Product field',
        meaning: 'Meaning',
        choose: 'Choose a field',
        chooseMeaning: 'Choose a meaning',
        empty: 'This product has no custom fields to correct. Standard product fields are shown below.',
        unavailable: 'Product fields have not been checked. Reload them to see available comparisons.',
        partial: 'Some product fields could not be loaded. Available choices may be incomplete.',
        reload: 'Reload product fields',
        close: 'Close',
        title: 'What would you like to test?',
        hint: 'Choose a comparison supported by this product.',
        nothing: 'No comparisons are available for this product yet.',
        enable: 'Review field meanings to enable this comparison.',
        comparisonMeaningChanged: 'Product field changes affect the selected comparison. Your comparison writing will be kept.',
        identityUnverifiable: 'The original field for this comparison cannot be verified. Replace or remove the comparison.',
        fieldMismatch: 'This comparison no longer matches an available product field. Replace or remove it; your writing is still here.',
        blocked: 'Replace or remove comparisons that no longer match their product fields. Your writing is still here.',
    },
    /** The "What would you like to test?" field picker: the list on the left and the detail on the right. */
    fieldPicker: studyBuilderFieldPickerCopy,
    imageComparisonGuidance: {
        title: 'Compare two ways of showing your product',
        description: 'Try a product-only photo alongside a photo showing the product in use.',
        original: 'Product only',
        originalDescription: 'Show the packaging and shape clearly.',
        alternative: 'Product in use',
        alternativeDescription: 'Show how someone would use or enjoy it.',
        note: 'Illustrative examples. Choose your own photos using the image controls below.',
    },
    preparedSetupsPanel: {
        refreshingPreparedSetups001: 'Refreshing prepared setups...',
        preparedStudySetups002: 'Prepared test setups',
        noPreparedSetupsYet003: 'No prepared setups yet',
        createNewStudy004: 'Create new test',
        prepareAProductStudySetup005: 'Prepare a product test setup before creating a TestFeed draft.',
        noSetupsMatchTheSelected006: 'No setups match the selected filter.',
        clearFilters007: 'Clear filters',
        savedDraftsTitle: 'Saved test drafts',
        savedDraftsDescription: 'Continue a test you have not prepared yet.',
        resumeDraftAction: 'Resume',
        opportunityDraftLabel: 'From an opportunity',
        savedDraftLabel: 'Saved draft',
        untitledDraft: 'Untitled test',
    },
    studyBriefFields: {
        studyName001: 'Test name',
        decisionToMake002: 'Decision to make',
        thePracticalDecisionThisSetup003: 'The practical decision this setup should help you make.',
        objective004: 'Objective',
        theResearchQuestionThisProduct005: 'The research question this product test should answer.',
        hypothesis006: 'Hypothesis',
        oneOrMoreExpectationsTo007: 'One or more expectations to check. Put each hypothesis on its own line.',
        diagnosticPrompt008: 'Your question',
        openEndedDiagnosticQuestionIt009: "A written-answer question. Answers come back in shoppers' own words and do not change the test score.",
    },
    studySummarySidebar: {
        heading: 'Summary',
        noProduct: 'No product chosen yet',
        testingHeading: "What you're testing",
        nothingToTest: 'Nothing to compare yet',
        audienceHeading: 'Who',
        notChosen: 'Not chosen yet',
        gender: { All: 'All genders', Female: 'Women', Male: 'Men' },
        shoppersHeading: 'Shoppers and time',
        versionCountUnavailable: 'Version count unavailable',
        shopperCountUnavailable: 'Shopper count unavailable',
        noCreditPlan: 'No credit plan configured',
        creditsUnavailable: 'Credits could not be checked. Try again.',
        timingUnavailable: 'Recent test times could not be checked. Try again.',
        timingNotEnoughHistory: 'Time estimate available after three completed tests.',
    },
    studyBuilderHelpers: {
        discardDraft001: 'Discard draft',
    },
    studyBuilderPanel: {
        productStudyForm001: 'product-study-form',
        reconnectApp002: 'Reconnect app',
        savingEllipsis003: 'Saving…',
        save004: 'Save',
        discardingEllipsis005: 'Discarding…',
        discard006: 'Discard',
        loadingSetup007: 'Loading setup…',
    },
    ...studyBuilderPageCopy,
    studyBuilderSurveyStep: {
        studyTitle001: 'Test title',
        giveThisStudyAName002: 'Give this test a clear name so you can find it later.',
        chooseProduct: 'Choose product',
        changeProduct: 'Change product',
        productDescriptionLabel: 'Product description (shown to shoppers)',
        productDescriptionHelp: 'Shoppers will read this description as the baseline context before answering test questions.',
        studyTitlePlaceholder: 'e.g. DD Cream Clarity Test',
        studyGoalLabel: 'Test goal',
        studyGoalPlaceholder: 'e.g. Assess whether the product message makes shoppers understand and want to buy.',
        productContext001: 'Product context',
        shopifyDetailsUsedToAnchor002: 'Shopify details used to anchor the product story.',
        selectAShopifyProductTo004: 'Select a Shopify product to anchor this test before you continue.',
        productRequiredBeforeComparison005: 'Select a product before adding something to compare.',
        productLockedHelp006: 'This test is tied to this product. You can still change the audience and the comparison text. Discard the draft to start with a different product.',
    },
    /** The variant section: picking which existing variant of the product the test is about. Plain word: "variant". */
    studyBuilderVersionStep: {
        heading: 'Which variant are you testing?',
        description: 'Pick the exact variant a shopper buys. Its price and picture anchor the test.',
        chooseVersionToTest: 'Choose a variant to test.',
        searchLabel: 'Search variants',
        searchPlaceholder: 'Filter by option or price',
        showMore: 'Show more',
        showFewer: 'Show fewer',
        outOfStock: 'Out of stock',
    },
    studySetupCard: {
        studyDraftWasCreatedIn001: 'Test draft was created in TestFeed. Refresh status if the link is still loading.',
        viewResults002: 'View results',
        deleteSetup003: 'Delete setup',
        newVersion004: 'New version',
        studyRunInProgress005: 'Test run in progress',
        contactSupport006: 'Contact support',
        deletionInProgress011: 'Deletion in progress',
        deletionInProgressBody012: 'This test is being permanently deleted. You can leave this page.',
        deletionNeedsAttention013: 'Deletion needs attention',
        deletionFailedBody014: "TestFeed couldn't finish deleting this test. Contact support for help.",
        deletionStatusUnavailable015: 'Deletion status unavailable',
        deletionStatusUnavailableBody016: "We couldn't check the deletion status. The setup is still listed. Refresh the page to try again.",
        deletionBlocked017: "This setup can't be deleted here",
        deletionBlockedBody018: 'TestFeed needs to review the linked test before this setup can be removed. Contact support for help.',
        deletionCompleted019: 'Deletion complete',
        deletionCompletedBody020: 'The test is deleted. Removing this setup from Shopify…',
        deletionFinaliseFailed021: 'Shopify cleanup needs attention',
        deletionFinaliseFailedBody022: 'The test is deleted, but this setup is still listed. Refresh the page or contact support.',
        deletionFinalisingLabel023: 'Removing setup from Shopify',
        editSetup024: 'Edit setup',
        runStudy025: 'Run test',
        tryAgain026: 'Try again',
    },
    studySetupAudience: {
        viewDetailsFor001: 'View audience details for',
        market002: 'Market',
        gender003: 'Gender',
        age004: 'Age',
        income005: 'Income',
        employment006: 'Employment',
        viewAudience007: 'View audience',
    },
    studySetupDeleteDialog: {
        title001: 'Delete test setup?',
        productLabel002: 'Setup',
        description003: "This permanently deletes the setup and its test data. You can't cancel after you confirm.",
        cancel004: 'Keep setup',
        confirm005: 'Delete permanently',
    },
    studyNewVersionDialog: {
        title001: 'Create a new version',
        description002: 'This opens the test builder pre-filled from this setup and creates a new linked version. Tell us what you are changing so results can be compared correctly.',
        whatChangedLabel003: 'What are you changing?',
        changedStimulus004: 'Product, version, or setup changed',
        changedAudience005: 'Audience changed',
        changedMethod006: 'Method or settings changed',
        other007: 'Something else',
        cancel008: 'Cancel',
        confirm009: 'Continue to builder',
        reasonRequired010: 'Choose what changed before continuing.',
    },
    studySetupsPage: {
        allStudies001: 'All tests',
        needsReview002: 'Drafts',
        inProgress003: 'Running',
        resultsReady004: 'Complete',
        needsAttention005: 'Errors',
        allStudiesHeading006: 'All tests',
        needsReviewHeading007: 'Drafts',
        inProgressHeading008: 'Running',
        resultsReadyHeading009: 'Complete',
        needsAttentionHeading010: 'Errors',
        searchStudies011: 'Search tests',
        studyHeading012: 'Test name',
        statusHeading013: 'Status',
        audienceHeading014: 'Audience',
        createdHeading015: 'Created',
        actionsHeading016: 'Actions',
        moreActionsFor017: 'More actions for',
        sortStudyTitleAscending018: 'Test name A–Z',
        sortStudyTitleDescending019: 'Test name Z–A',
        sortStatusAscending020: 'Status A–Z',
        sortCreatedDescending021: 'Created newest',
        sortCreatedAscending022: 'Created oldest',
        productHeading023: 'Product',
        productImageHeading024: 'Product image',
        untitledStudy025: 'Untitled test',
        sortProductTitleAscending026: 'Product title A–Z',
        sortProductTitleDescending027: 'Product title Z–A',
        treatmentTypeHeading028: "What's tested",
        treatmentsHeading029: 'Versions',
        sortTreatmentsAscending030: 'Versions low to high',
        sortTreatmentsDescending031: 'Versions high to low',
        treatmentTypeClaim032: 'Claim / message',
        treatmentTypeValueProposition033: 'What you say about it',
        treatmentTypeDescription038: 'Product description',
        treatmentTypePrice034: 'Price point',
        treatmentTypePromotion035: 'Offer',
        treatmentTypeImage036: 'Image / creative',
        unavailableValue037: '—',
        filtersButton038: 'Filters',
    },
    studySurveyBuilderTestHelpers: {
        merchantObject001: 'Merchant object',
    },
    researchInputPicker: {
        loadFailed: "We couldn't load this product's fields. Please try again.",
        productField: 'Product field',
        customField: 'Custom field',
    },
    /** The page-level summary and the checklist beside Submit. The lines inside them come from the Worker. */
    studyBuilderStudyBuilderValidation: {
        surveyAuthoringNeedsCorrection001: 'Survey authoring needs correction',
        fixSurveyAuthoring004: 'Fix survey authoring',
        runStudy005: 'Save setup',
        refreshReview006: 'Refresh Review',
        savingSetup007: 'Saving setup',
    },
    studySurveyBuilderComparisonVariableRows: {
        addOption: 'Add option',
        versionLimit: 'Up to {count} versions',
        priceOptionHelp: 'Enter a price for each option.',
        remove002: 'Remove',
        /** The accessible name for an option's Remove button, so each one is distinct. `{label}` is the option ("Option A"). */
        removeOptionAccessibility: 'Remove {label}',
        max003: '(Max',
        /** The resolved price-entry currency, shown subdued above the price rows. `{symbol}`/`{code}` are filled by the Worker. */
        pricesInCurrency048: 'Prices in {symbol} ({code})',
        descriptionFieldLabel: 'Product description',
        descriptionReplaceHelp: 'Each option replaces the complete description.',
        targetAppendDescription: 'If this wins, you can add it to your product description from the results.',
        targetReplaceDescription: 'If this wins, you can replace your product description from the results.',
        targetSetPrice: 'If this wins, you can update your product price from the results.',
        targetAddFeaturedMedia: 'If this wins, you can make it your featured image from the results.',
        benchmarkBadge004: 'Current',
        benchmarkLabel006: 'Current product',
        currentImageBadge007: 'Current',
        currentImageLabel008: 'Current image',
        addImageTile010: 'Add image',
        replaceImageAction021: 'Replace',
        noImageSelected022: 'No image selected',
        imageTileAlt011: 'Comparison image',
        uploadingImage012: 'Uploading',
        uploadFailedError015: 'Upload failed',
        missingMediaUrlError016: 'No media URL returned',
        removeComparisonDialogTitle017: 'Remove this comparison?',
        removeComparisonDialogBody018: 'This is the last option in this comparison, so removing it deletes the whole comparison. Any survey questions linked to it will be unlinked, and any of their wording that follows our suggested phrasing will reset to the default.',
        removeComparisonDialogConfirm019: 'Remove comparison',
        removeComparisonDialogCancel020: 'Keep comparison',
        suggestVariationsAction023: 'Suggest test variations',
        showAnotherVariationSet027: 'Show another set',
        introductions: {
            claim: [
                'A few fresh ways to say it',
                'Get three message ideas based on your product. Choose one, make it your own, and add it to your test.',
            ],
            description: [
                'Rewrite the full product description',
                'Get three complete description ideas based on your product. Choose one, make it your own, and add it to your test.',
            ],
            offer_pricing: [
                'Make your offer easy to understand',
                'Explore alternative offer ideas. Review the terms before adding an idea to your test.',
            ],
            usage_experience: [
                'Show how it fits into everyday life',
                'Explore ways to describe using your product. Review and edit each idea before adding it.',
            ],
            ingredient_proof: [
                'Put your product details into words',
                'Explore messages about your ingredients and evidence. Check each claim before using it.',
            ],
            price: [
                'Compare a different price',
                'Choose a calculated price below, then edit it if needed. Your store price stays unchanged.',
            ],
            visual_concept: [
                'Show another side of your product',
                'Choose another photo to compare with the original. Try a close-up, a different angle, or the product in use.',
            ],
        },
        suggestVariationsHeading024: 'Choose an idea to add.',
        suggestVariationsUseIdea: 'Use this idea',
        suggestVariationsAddedIdea: 'Added',
        suggestVariationsPending028: 'Preparing test variations…',
        suggestVariationsCap029: "You've seen all available sets. Showing an earlier set.",
        suggestVariationsError026: "Couldn't suggest test variations. Try again.",
        suggestVariationsNoCredits: "You don't have enough credits to suggest test variations.",
        suggestVariationsNoBaseline: 'This field has no current text to rewrite. Add copy to the product in Shopify, then ask for test variations.',
        suggestVariationsStaleEdit: 'That suggestion set has changed. Ask for suggestions again.',
        suggestVariationsWrongField: "This field can't use a wording idea.",
        suggestVariationsStillPreparing034: 'Still preparing ideas. Check again in a moment.',
        suggestVariationsCheckAgain035: 'Check again',
        suggestionVoteUp030: 'Thumbs up',
        suggestionVoteDown031: 'Thumbs down',
        suggestionFeedbackTitle033: 'How was this suggestion?',
        suggestionFeedbackReasons034: 'What was wrong? (optional)',
        suggestionFeedbackComment035: 'Tell us more (optional)',
        suggestionFeedbackSubmit036: 'Submit feedback',
        suggestionFeedbackCancel037: 'Cancel',
        suggestionFeedbackError038: "Couldn't save feedback. Try again.",
        suggestionFeedbackSaved039: 'Feedback submitted.',
        suggestionFeedbackNextSet050: 'Your feedback will guide the next set.',
        suggestionFeedbackMarkedHelpful051: 'You marked this suggestion helpful.',
        suggestionFeedbackMarkedNotHelpful052: 'You marked this suggestion not helpful.',
        suggestionFeedbackReasonLimit040: 'Choose up to 3 reasons.',
        suggestionFeedbackReasonOffBrand041: 'Off-brand',
        suggestionFeedbackReasonRepeats042: 'Repeats the current text',
        suggestionFeedbackReasonInaccurate043: 'Inaccurate',
        suggestionFeedbackReasonPoorTone044: 'Poor tone',
        suggestionFeedbackReasonNotUsable045: 'Not usable',
        suggestionFeedbackReasonUnsafe046: 'Unsafe or inappropriate',
        suggestionFeedbackPositiveReasons053: 'What worked? Choose at least one.',
        suggestionFeedbackReasonOnBrand054: 'Feels on-brand',
        suggestionFeedbackReasonClearerBenefit055: 'Clearer benefit',
        suggestionFeedbackReasonUsefulDetail056: 'Useful product detail',
        suggestionFeedbackReasonAppealingTone057: 'More appealing tone',
        suggestionFeedbackReasonDistinct058: 'More distinct from the current text',
        suggestionFeedbackCommentLimit048: 'Keep your comment under 500 characters.',
        suggestionFeedbackResponsePrompt049: 'Your response',
        sourceFieldCaption047: 'Starting point from your custom field (not shown to shoppers):',
    },
    studySurveyBuilderMethodRoutingReview: {
        allowedOutput001: 'Allowed output',
        blockedClaim002: 'Blocked claim',
        methodReview003: 'Question review',
    },
    studySurveyBuilderStimulusAuthoringSection: {
        generalStimulusContext001: 'What shoppers see',
        studySetup002: 'Test setup',
        describeWhatShoppersShouldReact003: 'Describe what shoppers should react to and what you want to compare.',
        productStory004: 'Product story',
        productDescription013: 'Product description',
        describeTheProductOfferOr005: 'Describe the product, offer, or idea every shopper should see before answering.',
        whatToCompare006: 'What to compare',
        oneComparisonHelpText007: 'Add options inside this comparison. Shopify tests support one comparison at a time.',
        chooseOneComparisonHelpText008: 'Choose one claim, value proposition, price, image, or offer comparison for this test.',
        chooseFromProduct: 'Choose from this product',
        selectProductFirst: 'Select a Shopify product above before adding options to compare.',
        chooseInputToStart: 'Choose a research input from this product to start a comparison.',
        comparisonSelected009: 'Comparison selected',
        addComparisonEllipsis010: 'Add comparison...',
    },
    studySurveyBuilderSurveyBuilderErrorBoundary: {
        unableToLoadSurveyBuilder001: 'Unable to load Test builder',
        resetAndRestartWizard002: 'Reset and restart wizard',
        aRenderingConstraintValidationError003: 'Something went wrong setting up this step.',
    },
    wizardWizard: {
        step001: 'Step',
        of002: 'of',
    },
};
//# sourceMappingURL=study.js.map